import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Download, PenLine, ShieldCheck, Upload, CheckCircle2, FileWarning } from "lucide-react";
import { usePortalConsent } from "../../application/usePortalConsent";
import SignaturePad, { type SignaturePadHandle } from "../components/SignaturePad";
import ConsentCopiesList from "../../../../shared/ui/consent/ConsentCopiesList";
import Button from "../../../../shared/ui/button/Button";

export default function ConsentPage() {
  const { t } = useTranslation();
  const signatureRef = useRef<SignaturePadHandle>(null);

  const {
    doc,
    loading,
    acknowledged,
    setAcknowledged,
    submitting,
    showRevoke,
    setShowRevoke,
    error,
    selectedFile,
    setSelectedFile,
    uploading,
    uploadSuccess,
    setUploadSuccess,
    uploadError,
    setUploadError,
    fileInputRef,
    documentUrl,
    documentFailed,
    hasSignature,
    setHasSignature,
    copies,
    downloadingCopyId,
    accept: handleAccept,
    sign,
    downloadCopy: handleDownloadCopy,
    upload: handleUpload,
    revoke: handleRevoke,
  } = usePortalConsent();

  const handleSign = () => sign(signatureRef.current?.toDataURL());

  if (loading) {
    return (
      <>
        <div className="text-gray-500 py-10">{t("common.loading")}</div>
      </>
    );
  }

  if (!doc) {
    return (
      <>
        <div className="text-gray-500 py-10">{t("portal.consent.unavailable")}</div>
      </>
    );
  }

  const isSigned = doc.signature_method === "drawn" && Boolean(doc.signed_at);

  return (
    <>
      <div className="max-w-3xl mx-auto">
      <div className="glass-effect glass-border border rounded-2xl shadow-sm flex flex-col gap-6 p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-content-primary">
              {t("portal.consent.title")}
            </h1>
            <p className="text-xs text-content-tertiary mt-1">
              {t("portal.consent.version")} {doc.version}
            </p>
          </div>
          {documentUrl && (
            <a
              href={documentUrl}
              download={`consentimiento-${doc.version}.pdf`}
              className="flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 shrink-0"
            >
              <Download className="w-4 h-4" />
              {t("portal.consent.download")}
            </a>
          )}
        </div>

        {/* The document itself, as the backend renders the stored version */}
        {documentUrl && (
          <iframe
            src={documentUrl}
            className="w-full rounded-lg border border-gray-200 dark:border-gray-700 h-[60vh] md:h-[520px]"
            title={t("portal.consent.title")}
          />
        )}
        {documentFailed && (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 py-12 px-6 text-center">
            <FileWarning className="w-8 h-8 text-content-tertiary" />
            <p className="text-sm font-medium text-content-secondary">
              {t("portal.consent.unavailable")}
            </p>
            <p className="text-xs text-content-tertiary max-w-sm">
              {t("portal.consent.documentUnavailable")}
            </p>
          </div>
        )}

        {doc.requires_acceptance && (
          <>
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-content-secondary">
                {t("portal.consent.acknowledgeLabel")}
              </span>
            </label>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <PenLine className="w-4 h-4 text-brand-500 shrink-0" />
                <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                  {t("portal.consent.signatureTitle")}
                </h2>
              </div>
              <SignaturePad
                ref={signatureRef}
                label={t("portal.consent.signatureCanvasLabel")}
                clearLabel={t("portal.consent.signatureClear")}
                hint={t("portal.consent.signatureHint")}
                disabled={submitting}
                onChange={setHasSignature}
              />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex flex-col gap-2">
              <Button
                onClick={handleSign}
                disabled={!acknowledged || !hasSignature}
                loading={submitting}
                className="w-full justify-center"
              >
                {t("portal.consent.signAndAccept")}
              </Button>
              {/* Drawing takes a pointer and a steady hand, so it is never the
                  only way to consent: whoever cannot draw a signature can still
                  accept, on the same evidence the portal always recorded. */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAccept}
                disabled={!acknowledged || submitting}
                className="w-full justify-center"
              >
                {t("portal.consent.acceptWithoutSignature")}
              </Button>
            </div>
          </>
        )}

        {!doc.requires_acceptance && (
          <div className="flex flex-col gap-3">
            {/* Accepted status card */}
            <div className="flex items-center gap-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-4 py-3">
              <ShieldCheck className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-700 dark:text-green-400">
                  {isSigned ? t("portal.consent.signedBadge") : t("portal.consent.alreadyAccepted")}
                </p>
                {isSigned && doc.signed_at && (
                  <p className="text-xs text-green-700/80 dark:text-green-400/80">
                    {t("portal.consent.signedAt", {
                      date: new Date(doc.signed_at).toLocaleString(),
                    })}
                  </p>
                )}
              </div>
            </div>

            {/* Upload signed consent */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Upload className="w-4 h-4 text-brand-500 shrink-0" />
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                  {t("portal.consent.uploadTitle")}
                </h3>
                {(doc.has_signed_document || uploadSuccess) && (
                  <span className="ml-auto flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {t("portal.consent.uploadedBadge")}
                  </span>
                )}
              </div>
              <p className="text-xs text-content-tertiary">
                {t("portal.consent.uploadDescription")}
              </p>

              {uploadSuccess && (
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  {t("portal.consent.uploadSuccess")}
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,image/jpeg,image/png"
                  className="hidden"
                  onChange={(e) => {
                    setSelectedFile(e.target.files?.[0] ?? null);
                    setUploadSuccess(false);
                    setUploadError(null);
                  }}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-brand-600 border-brand-200 hover:bg-brand-50"
                >
                  {doc.has_signed_document ? t("portal.consent.replaceUpload") : t("portal.consent.uploadButton")}
                </Button>
                {selectedFile && (
                  <>
                    <span className="text-xs text-gray-500 truncate max-w-[180px]">{selectedFile.name}</span>
                    <Button
                      size="sm"
                      onClick={handleUpload}
                      loading={uploading}
                      disabled={uploading}
                    >
                      {uploading ? t("portal.consent.uploading") : t("portal.consent.uploadSubmit")}
                    </Button>
                  </>
                )}
              </div>
              {uploadError && <p className="text-xs text-red-600">{uploadError}</p>}
            </div>

            {!showRevoke ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowRevoke(true)}
                className="self-start text-red-500 border-red-200 hover:bg-red-50"
              >
                {t("portal.consent.revokeLink")}
              </Button>
            ) : (
              <div className="border border-red-200 dark:border-red-900 rounded-xl p-4 flex flex-col gap-3">
                <p className="text-sm font-medium text-red-700 dark:text-red-400">
                  {t("portal.consent.revokeWarning")}
                </p>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleRevoke}
                    loading={submitting}
                  >
                    {submitting
                      ? t("portal.consent.revoking")
                      : t("portal.consent.revokeConfirm")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowRevoke(false)}
                  >
                    {t("common.cancel")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <ConsentCopiesList
          copies={copies}
          onDownload={handleDownloadCopy}
          downloadingId={downloadingCopyId}
        />
      </div>
      </div>
    </>
  );
}
